import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Grid,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { BillType, ORDER_TYPE, TAKEAWAY_STATUS } from '../../../all-types';
import { deliveryOptions } from '../../../constants';
import { CommonBtn } from '../../../components';
import { updateTakeawayBillStatus } from '../../../services';
import { BillPrinter } from '../../printer';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.65rem',
  height: 22,
  fontWeight: 700,
  textTransform: 'capitalize',
}));

const OrderTypeBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  fontSize: '0.75rem',
  fontWeight: 'medium',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
}));

type TakeawayCardProps = {
  order: BillType;
  onStatusChange?: (id: string, status: TAKEAWAY_STATUS | 'DONE') => void;
};

const TakeawayCard: React.FC<TakeawayCardProps> = ({ order, onStatusChange }) => {
  const theme = useTheme();

  const getTypeTextColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.freeTable.main;
      case ORDER_TYPE.TAKEAWAY:
        return '#E7AA5A';
      default:
        return 'default';
    }
  };

  const getTypeBorderColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return '#ADEED8';
      case ORDER_TYPE.TAKEAWAY:
        return '#FFE6BB';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.color8.main;
      case ORDER_TYPE.TAKEAWAY:
        return '#FFF9ED';
      default:
        return 'default';
    }
  };

  const getInstructions = (inst: string) => {
    try {
      const instructions: { [key: string]: string } = JSON.parse(inst);
      return (
        Object.entries(instructions)
          ?.map(([key, value]) => `${key}: ${value}`)
          .join(', ') || ''
      );
    } catch {
      return '';
    }
  };

  if (!order) return null;

  const getTimeSinceOrder = () => {
    const orderTime = Number(order?.timeInfo?.timestamp || 0);
    const now = Date.now();
    const diffInMinutes = Math.max(0, Math.floor((now - orderTime) / (1000 * 60)));
    return diffInMinutes;
  };

  const handleUpdate = async (status: TAKEAWAY_STATUS | 'DONE') => {
    try {
      if (order._id) {
        await updateTakeawayBillStatus(order._id, status);
        onStatusChange?.(order._id, status);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const isPending = order.takeAwayStatus === 'PENDING';
  const isReady = order.takeAwayStatus === 'READY';

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card
        elevation={4}
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRadius: 4,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(249,250,251,0.95) 100%)',
          border: (t) => `1px solid ${t.palette.divider}`,
          '&:hover': { boxShadow: 3 },
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 2.25 }}>
          <Stack spacing={1.25}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1.25} alignItems="center">
                <img
                  src={deliveryOptions[order?.takeAwayType || 'Take-Away/Parcel']?.logoUrl}
                  alt={order?.takeAwayType}
                  style={{ width: 36, height: 36, borderRadius: 8 }}
                />
                <Typography variant="subtitle1" fontWeight={800}>
                  {order?.takeAwayType}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1}>
                <StatusChip
                  label={(order.takeAwayStatus || 'PENDING').toLowerCase()}
                  sx={{
                    bgcolor: isReady ? theme.palette.success.light : theme.palette.occupied.main,
                    color: isReady ? 'success.dark' : 'warning.dark',
                  }}
                />
                <StatusChip
                  label={order.modeOfPayment === 'CASH' ? 'Cash' : 'Paid'}
                  sx={{
                    bgcolor:
                      order.modeOfPayment === 'CASH'
                        ? theme.palette.occupied.main
                        : theme.palette.success.light,
                    color:
                      order.modeOfPayment === 'CASH'
                        ? 'warning.dark'
                        : 'success.dark',
                  }}
                />
              </Stack>
            </Stack>

            {/* Meta */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" fontWeight={700}>
                Order #{order?.sNo}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} color="text.secondary">
                <AccessTimeIcon fontSize="small" />
                <Typography variant="caption">{getTimeSinceOrder()} mins</Typography>
              </Stack>
            </Stack>

            <Divider />

            {/* Items */}
            <Stack spacing={1}>
              {order.items?.map((item: any, index) => (
                <Stack key={item._id || `${index}-${item.itemName}`} spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">
                      {index + 1}• {item.itemName} (x {item.noOfItems})
                    </Typography>
                    <Typography variant="caption">
                      {item.quantity <= 1 ? '' : item.quantity} ({item.quantityType})
                    </Typography>
                  </Stack>
                  {!!item?.kitchInfo?.kitchenInstrcutions && (
                    <Typography variant="caption" color="text.secondary">
                      {getInstructions(item?.kitchInfo?.kitchenInstrcutions)}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight="bold">
              Amount: <Box component="span" fontWeight="bold">₹{order.billingInfo?.finalBillAmount || 0}</Box>
            </Typography>
          </Stack>
        </CardContent>

        {/* Footer actions: single row */}
        <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
          {/* Print button (always) */}
          <Box>
            <BillPrinter bill={order} takeawayKitchenPrint />
          </Box>

          {/* State action (right) */}
          {isPending && (
            <CommonBtn variant="contained" endIcon={<CheckCircleIcon />} onClick={() => handleUpdate(TAKEAWAY_STATUS.READY)}>
              Mark as Ready
            </CommonBtn>
          )}
          {isReady && (
            <CommonBtn variant="contained" color="success" endIcon={<DoneAllIcon />} onClick={() => handleUpdate('DONE')}>
              Mark as Complete
            </CommonBtn>
          )}
        </CardActions>

        <OrderTypeBadge
          sx={{
            bgcolor: `${getTypeColor(order.orderType)}`,
            color: `${getTypeTextColor(order.orderType)}`,
            borderTop: `1px dashed ${getTypeBorderColor(order.orderType)}`,
          }}
        >
          <Typography variant="caption" textTransform="capitalize" fontWeight={700}>
            {order.orderType.toLowerCase()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {getTimeSinceOrder()} mins
            </Typography>
          </Box>
        </OrderTypeBadge>
      </Card>
    </Grid>
  );
};

export { TakeawayCard };
